import { useMemo } from 'react';

import {
  composeCalendar,
  withCalendarState,
  CALENDAR_SELECTION_TYPE,
} from '@skyscanner-internal/backpack-web/bpk-component-calendar';
import { useDispatch, useSelector } from 'react-redux';

import { useLocalisation } from '../../../../../services/contextAdaptor/LocalisationContext';
import { clearTripDuration } from '../../../actions/trip-duration-actions';
import { FLIGHT_VERTICAL } from '../../../helpers/constants/analyticsConstants';
import { GRID_SOURCE } from '../../../helpers/constants/dataTypeConstants';
import { useDateSelection } from '../../../helpers/hooks/useDateSelection';
import { trackInteractionEvent } from '../../../helpers/logging/trackInteractionEvent';
import { getCalendarFormattedPrice } from '../../../helpers/presentation/calendarCurrencyFormatter';
import {
  getFirstDayOfWeek,
  getMinCalendarDate,
  getSelectedDate,
} from '../../../selectors';
import { getCalendarNav } from '../../CalendarNav/CalendarNav';
import CalendarGridHeader from '../CalendarGridHeader';
import GridWithLoading from '../GridWithLoading';

import { getComponentWithDirection as getDateComponentWithDirection } from './PricedCalendarDate';

import type { Direction, State } from '../../../selectors/types';

type MonthCalendarProps = {
  direction: Direction;
};

const MonthCalendar = ({ direction }: MonthCalendarProps) => {
  const dispatch = useDispatch();
  const { selectDay, selectMonth } = useDateSelection();
  const {
    daysOfWeek,
    formatCurrency,
    formatCurrencyShort,
    formatDateFull,
    formatMonth,
  } = useLocalisation();

  const selectedDate = useSelector((state: State) =>
    getSelectedDate(state, direction),
  );
  const minDate = useSelector((state: State) =>
    getMinCalendarDate(state, direction),
  );
  const weekStartsOn = useSelector(getFirstDayOfWeek);

  const onDateSelect = (date: Date) => {
    dispatch(clearTripDuration());
    selectDay(date.getDate(), direction, GRID_SOURCE, FLIGHT_VERTICAL);
    trackInteractionEvent({
      name: 'Calendar_Date_Selected',
    });
  };

  const onMonthChange = (
    _event: UIEvent,
    data: { month: Date; source: string },
  ) => {
    const date = data.month;
    const { source } = data;
    selectMonth(date.getMonth() + 1, date.getFullYear(), direction, source);
  };

  const ComposedCalendar = useMemo(() => {
    const cellPriceFormatter = (price: number) =>
      getCalendarFormattedPrice(price, {
        formatCurrency,
        formatCurrencyShort,
      }) ?? '';

    // composeCalendar only calls this component with an `id` prop, so we use
    // getCalendarNav to close over direction and adapt CalendarNav
    // to the shape composeCalendar expects.
    const CalendarNavComponent = ({ id: navId }: { id: string }) =>
      getCalendarNav({
        direction,
        id: navId,
      });

    return withCalendarState(
      composeCalendar(
        CalendarNavComponent,
        CalendarGridHeader,
        GridWithLoading,
        getDateComponentWithDirection(
          direction,
          cellPriceFormatter,
          formatDateFull,
        ),
      ),
    );
  }, [direction, formatCurrency, formatCurrencyShort, formatDateFull]);

  return (
    <ComposedCalendar
      id={direction}
      data-testid="month-view-outbound-calendar"
      onDateSelect={onDateSelect}
      onMonthChange={onMonthChange}
      formatMonth={formatMonth}
      formatDateFull={formatDateFull}
      daysOfWeek={daysOfWeek}
      selectionConfiguration={{
        date: selectedDate,
        type: CALENDAR_SELECTION_TYPE.single,
      }}
      fixedWidth={false}
      weekStartsOn={weekStartsOn}
      minDate={minDate}
    />
  );
};

export default MonthCalendar;
